import React from 'react'
import PropTypes from 'prop-types'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import i18n from 'simple-react-i18n'
import { Card, Grid2 } from '@mui/material'
import Input from '../../../../components/forms/Input'
import { getSetting, getSettingInt, getSettingJson, isTrue } from '../../../../utils/SettingUtils'
import Select from '../../../../components/forms/Select'
import Checkbox from '../../../../components/forms/Checkbox'
import { DEFAULT_CONTRIBUTOR_TYPE } from '../../user/constants/UserConstants'
import NumberField from '../../../../components/forms/NumberField'
import { CardTable } from '../../../../components/datatable/NewTable'
import { nbPerPageLabelTiny, SANDRE } from '../../../../referencial/constants/ReferencialConstants'
import { linkContributorsDepartments } from '../../../../carto/constants/CartoConstants'
import { compact } from 'lodash'
import { darkBlue } from '../../../../utils/constants/ColorTheme'
import { shallowEqual, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from '../../../../referencial/components/contributor/components/MultiContributorsAutocomplete'
import { hasValue } from '../../../../utils/NumberUtil'
import SieauParameterDto from '../../../dto/SieauParameterDto'
import LayerDto from '../../../../home/dto/LayerDto'
import useSandreList from '../../../../utils/customHook/useSandreList'

const JOB_PURGE_NB_DAYS = 90
const HEADERS = ['contributor', 'departments']

const SettingsPanel = ({
    applicationSettings = [],
    onChangeSettings = () => {},
    disabled = false,
    themeLayers = [],
}) => {
    const {
        contributors,
        contributorsIndex,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
    }), shallowEqual)

    const logLevelOption = [
        { value: '', label: '' },
        { value: '1', label: i18n.debug },
        { value: '2', label: i18n.information },
        { value: '3', label: i18n.warning },
        { value: '4', label: i18n.error },
    ]

    const editionNameOption = [
        { value: '', label: '' },
        { value: '1', label: i18n.login },
        { value: '2', label: i18n.loginAndCityName },
        { value: '3', label: i18n.cityNameAndLogin },
    ]

    const onChangeContributorsData = (values, actualValue, newValue) => {
        const json = JSON.stringify(compact(values.map((v) => {
            if (v.contributor === actualValue.contributor) {
                return newValue
            }
            return v
        })))
        onChangeSettings(linkContributorsDepartments, json)
    }

    const contributorTypeList = useSandreList(SANDRE.CONTRIBUTOR_TYPES).map(c => ({ ...c, name: `[${c.code}] ${c.name}` }))
    const dataContributorsDpts = getSettingJson(applicationSettings, linkContributorsDepartments) ?? []

    const dataContributorsTable = dataContributorsDpts.map((v) => {
        return {
            contributor: {
                value: disabled.disabled ? (contributorsIndex[v.contributor] || {}).name : (
                    <MultiContributorsAutocomplete
                        col={12}
                        options={contributors}
                        values={v.contributor}
                        label=''
                        onChange={c => onChangeContributorsData(dataContributorsDpts, v, { contributor: c, departments: v.departments })}
                        disabled={disabled.disabled}
                    />
                ),
                sortValue: v.contributor,
                style: { width: '100%' },
            },
            departments: {
                value: disabled.disabled ? v.departments : (
                    <Input
                        col={12}
                        value={v.departments}
                        onChange={d => onChangeContributorsData(dataContributorsDpts, v, { contributor: v.contributor, departments: d })}
                        placeholder={'ex : 44,85,92'}
                        disabled={!hasValue(v.contributor)}
                    />
                ),
                sortValue: v.departments,
                style: { width: '100%' },
            },
        }
    })

    return (
        <Card sx={{ padding: '0.5rem', borderRadius: '0 0 5px 5px' }}>
            <StyledFieldSet>
                <StyledLegend>{i18n.directories}</StyledLegend>
                <Grid2 container spacing={1}>
                    <Grid2 size={6}>
                        <Input
                            title={ i18n.modelDirectory }
                            value={ getSetting(applicationSettings, 'CheminModeles') }
                            onChange={ v => onChangeSettings('CheminModeles', v) }
                            { ...disabled }
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Input
                            title={ i18n.exportDirectory }
                            value={ getSetting(applicationSettings, 'CheminExport') }
                            onChange={ v => onChangeSettings('CheminExport', v) }
                            { ...disabled }
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Input
                            title={ i18n.editionDirectory }
                            value={ getSetting(applicationSettings, 'CheminEdition') }
                            onChange={ v => onChangeSettings('CheminEdition', v) }
                            { ...disabled }
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Select
                            label={i18n.editionName}
                            options={editionNameOption}
                            keyLabel='label'
                            onChange={v => onChangeSettings('Nommage_Fichier', v)}
                            value={ getSetting(applicationSettings, 'Nommage_Fichier')}
                            noNullValue
                            {...disabled}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Input
                            title={ i18n.documentDirectory }
                            value={ getSetting(applicationSettings, 'CheminDocuments') }
                            onChange={ v => onChangeSettings('CheminDocuments', v) }
                            { ...disabled }
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Input
                            title={ i18n.pictureDirectory }
                            value={ getSetting(applicationSettings, 'CheminPhotos') }
                            onChange={ v => onChangeSettings('CheminPhotos', v) }
                            { ...disabled }
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Input
                            title={ i18n.scriptRdirectory }
                            value={ getSetting(applicationSettings, 'CheminR') }
                            onChange={ v => onChangeSettings('CheminR', v) }
                            { ...disabled }
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Checkbox
                            label={i18n.stationDirectoryOrganization}
                            checked={ isTrue(getSetting(applicationSettings, 'docFolderOrganisation')) }
                            { ...disabled }
                            onChange={(e) => onChangeSettings('docFolderOrganisation', e ? i18n.yes : i18n.no)}
                        />
                    </Grid2>
                </Grid2>
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>{i18n.contributors}</StyledLegend>
                <Grid2 container spacing={1}>
                    <Grid2 size={6}>
                        <Select
                            label={i18n.referentContributorType}
                            options={contributorTypeList}
                            onChange={v => onChangeSettings('contributorTypeAdministrator', v.toString())}
                            value={getSetting(applicationSettings, 'contributorTypeAdministrator') || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR}
                            {...disabled}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Select
                            label={`${i18n.type} ${i18n.contributor} ${i18n.operator}`}
                            value={getSetting(applicationSettings, 'contributorTypeOperator') || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR }
                            options={contributorTypeList}
                            onChange={ v => onChangeSettings('contributorTypeOperator', v.toString()) }
                            { ...disabled }
                            keyLabel='name'
                        />
                    </Grid2>
                </Grid2>
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>{i18n.cartography}</StyledLegend>
                <Grid2 container spacing={1}>
                    <Grid2 size={6} container>
                        <Grid2 size={6}>
                            <Select
                                label={i18n.layerESU}
                                options={themeLayers}
                                noNullValue
                                keyLabel='name'
                                keyValue='id'
                                onChange={v => onChangeSettings('themeCartoESU', v.toString())}
                                value={getSettingInt(applicationSettings, 'themeCartoESU')}
                                {...disabled}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <Select
                                label={i18n.layerESO}
                                options={themeLayers}
                                noNullValue
                                keyLabel='name'
                                keyValue='id'
                                onChange={v => onChangeSettings('themeCartoESO', v.toString())}
                                value={getSettingInt(applicationSettings, 'themeCartoESO')}
                                {...disabled}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <Input
                                title={ i18n.arrestDepartments }
                                value={ getSetting(applicationSettings, 'arrestDepartments') }
                                onChange={ v => onChangeSettings('arrestDepartments', v) }
                                { ...disabled }
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <Input
                                title={ i18n.meteoAlertDepartments }
                                value={ getSetting(applicationSettings, 'meteoAlertDepartments') }
                                onChange={ v => onChangeSettings('meteoAlertDepartments', v) }
                                { ...disabled }
                            />
                        </Grid2>
                        <Grid2 size={12}>
                            <NumberField
                                title={i18n.numberOfStationsMaxOnMap}
                                value={getSettingInt(applicationSettings, 'cartoStationsMaxNumber') || 15000}
                                onChange={ v => onChangeSettings('cartoStationsMaxNumber', v.toString()) }
                                { ...disabled }
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2 size={6}>
                        <CardTable
                            title={i18n.linksContributorsDepartments}
                            rows={dataContributorsTable}
                            rowsPerPageOptions={nbPerPageLabelTiny}
                            headers={HEADERS}
                            lineActions={[{
                                icon: 'clear',
                                onClick: (c) => onChangeSettings(linkContributorsDepartments, JSON.stringify(compact(dataContributorsDpts.filter((v) => v.contributor !== c.contributor.sortValue)))),
                                displayed: !disabled.disabled,
                            }]}
                            actions={disabled.disabled ? [] : [{
                                onClick: () => onChangeSettings(linkContributorsDepartments, JSON.stringify([...dataContributorsDpts.filter((v) => v.contributor), { contributor: null }])),
                                icon: 'note_add',
                                tooltip: i18n.add,
                                color: 'white',
                            }]}
                            color={darkBlue}
                        />
                    </Grid2>
                </Grid2>
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>{i18n.others}</StyledLegend>
                <Grid2 container spacing={1}>
                    <Grid2 size={3}>
                        <Checkbox
                            label={i18n.microsoftOffice}
                            checked={ isTrue(getSetting(applicationSettings, 'exportExcel')) }
                            { ...disabled }
                            onChange={(e) => onChangeSettings('exportExcel', e ? '1' : '0')}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Checkbox
                            label={i18n.libreOffice}
                            checked={ isTrue(getSetting(applicationSettings, 'exportCalc')) }
                            { ...disabled }
                            onChange={(e) => onChangeSettings('exportCalc', e ? '1' : '0')}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Checkbox
                            label={i18n.csv}
                            checked={ isTrue(getSetting(applicationSettings, 'exportCSV')) }
                            { ...disabled }
                            onChange={(e) => onChangeSettings('exportCSV', e ? '1' : '0')}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Checkbox
                            label={i18n.exploitationAEP}
                            checked={ isTrue(getSetting(applicationSettings, 'ExploitationAEP')) }
                            { ...disabled }
                            onChange={(e) => onChangeSettings('ExploitationAEP', e ? i18n.yes : i18n.no)}
                        />
                    </Grid2>
                    <Grid2 size={4}>
                        <Select
                            label={i18n.logLevel}
                            options={logLevelOption}
                            noNullValue
                            keyLabel='label'
                            onChange={v => onChangeSettings('LogLevel', v)}
                            value={getSetting(applicationSettings, 'LogLevel')}
                            {...disabled}
                        />
                    </Grid2>
                    <Grid2 size={4}>
                        <NumberField
                            title={i18n.purgeJobDays}
                            onChange={v => onChangeSettings('jobPurgeNbDays', v)}
                            value={ getSettingInt(applicationSettings, 'jobPurgeNbDays') || JOB_PURGE_NB_DAYS }
                            {...disabled}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Checkbox
                            label={i18n.internationalContext}
                            checked={ isTrue(getSetting(applicationSettings, 'ContexteInternational')) }
                            { ...disabled }
                            onChange={(e) => onChangeSettings('ContexteInternational', e ? i18n.yes : i18n.no)}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Checkbox
                            label={i18n.dontSendMail}
                            checked={ isTrue(getSetting(applicationSettings, 'dontSendMailOnCreateUser')) }
                            { ...disabled }
                            onChange={(e) => onChangeSettings('dontSendMailOnCreateUser', e ? i18n.yes : i18n.no)}
                        />
                    </Grid2>
                </Grid2>
            </StyledFieldSet>
        </Card>
    )
}

SettingsPanel.propTypes = {
    applicationSettings: PropTypes.arrayOf(PropTypes.instanceOf(SieauParameterDto)),
    onChangeSettings: PropTypes.func,
    disabled: PropTypes.shape({
        active: PropTypes.bool,
        disabled: PropTypes.bool,
    }),
    themeLayers: PropTypes.arrayOf(PropTypes.instanceOf(LayerDto)),
}

export default SettingsPanel